import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, ShoppingCart, ArrowRight, Headphones, Send, CreditCard, ChevronLeft, Lock, Clock } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmptyState = () => (
  <div className="card-surface text-center py-16 px-6" data-testid="cart-empty">
    <ShoppingCart className="w-12 h-12 text-faded mx-auto mb-4" />
    <h2 className="text-xl font-semibold text-strong mb-2">Your cart is empty.</h2>
    <p className="text-sm text-muted mb-6">Browse our widgets and add a tier to start.</p>
    <Link
      to="/widgets/team-randomizer"
      className="btn-primary px-5 py-2.5 inline-flex items-center space-x-2 text-sm"
      data-testid="cart-empty-cta"
    >
      <span>See the Team Randomizer</span>
      <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);

export const Cart = () => {
  const { items, removeItem, toggleAssisted, total, assistedPrice, clearCart } = useCart();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState('stripe');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(null);

  const isAuthed = Boolean(user && user !== false);

  const buildPayload = () => ({
    items: items.map((i) => ({ sku: i.sku, assisted_deployment: i.assistedDeployment })),
    method,
    customer_name: name || user?.name || '',
    customer_email: email || user?.email || undefined,
    notes,
    origin_url: window.location.origin,
  });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setRequestSuccess(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/orders/checkout`, buildPayload(), {
        withCredentials: true,
      });
      if (data.method === 'stripe' && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (data.method === 'request') {
        clearCart();
        setRequestSuccess({ total: data.total });
      }
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] py-12 px-4 sm:px-6 lg:px-8" data-testid="cart-page">
      <div className="max-w-5xl mx-auto">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-muted hover:text-strong inline-flex items-center space-x-1 mb-6"
          data-testid="cart-back"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>Keep shopping</span>
        </button>
        <h1 className="text-3xl sm:text-4xl font-bold text-strong tracking-tight mb-2">Your Cart</h1>
        <p className="text-sm text-muted mb-8">Review your items, decide on Assisted Deployment, and choose how to check out.</p>

        {requestSuccess && (
          <div className="card-surface p-6 mb-8 border-green-500/30" data-testid="cart-request-success">
            <h2 className="text-lg font-semibold text-strong mb-2">Request sent ✅</h2>
            <p className="text-sm text-muted">
              We received your order request for <strong className="text-strong">${requestSuccess.total.toFixed(2)}</strong>.
              Look out for a reply from us shortly. We'll guide you through the rest.
            </p>
            <Link to="/" className="btn-secondary px-4 py-2 text-sm mt-4 inline-block">Back to home</Link>
          </div>
        )}

        {items.length === 0 && !requestSuccess ? (
          <EmptyState />
        ) : items.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
            {/* ── Line items ── */}
            <div className="space-y-4" data-testid="cart-items">
              {items.map((it) => (
                <div key={it.lineId} className="card-surface p-5" data-testid={`cart-line-${it.lineId}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-strong leading-snug">{it.name}</h3>
                      <p className="text-xs text-faded mt-0.5">SKU: {it.sku}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold text-strong" data-testid={`cart-line-price-${it.lineId}`}>
                        ${it.price.toFixed(2)}
                      </div>
                      <button
                        onClick={() => removeItem(it.lineId)}
                        className="text-xs text-faded hover:text-red-300 inline-flex items-center space-x-1 mt-1"
                        data-testid={`cart-remove-${it.lineId}`}
                      >
                        <Trash2 className="w-3 h-3" /><span>Remove</span>
                      </button>
                    </div>
                  </div>
                  <label className={`flex items-start space-x-3 p-3 rounded border cursor-pointer transition-colors ${it.assistedDeployment ? 'border-emphasis bg-white/[0.02]' : 'border-subtle hover:border-default'}`}>
                    <input
                      type="checkbox"
                      checked={it.assistedDeployment}
                      onChange={() => toggleAssisted(it.lineId)}
                      className="mt-1"
                      data-testid={`cart-assisted-toggle-${it.lineId}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center space-x-2">
                          <Headphones className="w-4 h-4 accent-green" />
                          <span className="text-sm font-medium text-strong">Assisted Deployment (1hr Zoom)</span>
                        </div>
                        <span className="text-sm font-bold text-strong">+${assistedPrice.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-muted mt-1">Optional. We set up the widget in OBS / TikTok LIVE Studio for you.</p>
                    </div>
                  </label>
                </div>
              ))}
            </div>

            {/* ── Order summary + checkout ── */}
            <aside className="card-surface p-6 h-fit sticky top-24" data-testid="cart-summary">
              <h2 className="text-base font-semibold text-strong mb-4">Order summary</h2>
              <ul className="space-y-2 mb-4 text-sm">
                {items.map((it) => (
                  <React.Fragment key={`sum-${it.lineId}`}>
                    <li className="flex justify-between text-default">
                      <span className="pr-2 line-clamp-2">{it.name}</span>
                      <span className="shrink-0">${it.price.toFixed(2)}</span>
                    </li>
                    {it.assistedDeployment && (
                      <li className="flex justify-between text-muted text-xs pl-3" data-testid={`cart-summary-assisted-${it.lineId}`}>
                        <span>+ Assisted Deployment</span>
                        <span>${assistedPrice.toFixed(2)}</span>
                      </li>
                    )}
                  </React.Fragment>
                ))}
              </ul>
              <div className="border-t border-subtle pt-4 mb-5 flex justify-between text-base">
                <span className="font-medium text-strong">Total</span>
                <span className="font-bold text-strong" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>

              <form onSubmit={submit} className="space-y-3" data-testid="cart-checkout-form">
                {!isAuthed && (
                  <div
                    className="p-4 rounded border border-emphasis space-y-3 mb-2"
                    style={{ background: 'rgba(57, 255, 20, 0.05)' }}
                    data-testid="cart-auth-gate"
                  >
                    <div className="flex items-start space-x-2">
                      <Lock className="w-4 h-4 accent-green shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-strong mb-1">Account required</p>
                        <p className="text-xs text-muted leading-relaxed">
                          Every order needs an account so your purchased widgets land in your dashboard. It takes 30 seconds.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link
                        to={`/register?next=${encodeURIComponent('/cart')}`}
                        className="btn-primary px-3 py-2 text-xs text-center font-semibold"
                        data-testid="cart-auth-register"
                      >
                        Create account
                      </Link>
                      <Link
                        to={`/login?next=${encodeURIComponent('/cart')}`}
                        className="btn-secondary px-3 py-2 text-xs text-center font-semibold"
                        data-testid="cart-auth-login"
                      >
                        Sign in
                      </Link>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3 p-3 rounded border border-default" style={{ background: 'rgba(0, 240, 255, 0.05)' }} data-testid="cart-24hr-notice">
                  <Clock className="w-4 h-4 accent-cyan shrink-0 mt-0.5" />
                  <p className="text-xs text-default leading-relaxed">
                    Once your order is complete, your widgets may take <strong className="text-strong">up to 24 hours</strong> to appear in your account page.
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-faded">Choose how to check out</p>
                  <label className={`flex items-start space-x-2 p-3 rounded border cursor-pointer transition-colors ${method === 'stripe' ? 'border-emphasis bg-white/[0.02]' : 'border-subtle hover:border-default'}`}>
                    <input type="radio" name="method" value="stripe" checked={method === 'stripe'} onChange={() => setMethod('stripe')} className="mt-1" data-testid="cart-method-stripe" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-strong flex items-center space-x-2">
                        <CreditCard className="w-4 h-4 accent-green" /><span>Pay now (Stripe)</span>
                      </div>
                      <p className="text-xs text-muted mt-0.5">Secure card payment, instant confirmation.</p>
                    </div>
                  </label>
                  <label className={`flex items-start space-x-2 p-3 rounded border cursor-pointer transition-colors ${method === 'request' ? 'border-emphasis bg-white/[0.02]' : 'border-subtle hover:border-default'}`}>
                    <input type="radio" name="method" value="request" checked={method === 'request'} onChange={() => setMethod('request')} className="mt-1" data-testid="cart-method-request" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-strong flex items-center space-x-2">
                        <Send className="w-4 h-4 accent-cyan" /><span>Send request to LAB21G</span>
                      </div>
                      <p className="text-xs text-muted mt-0.5">We'll email you to confirm details and arrange payment.</p>
                    </div>
                  </label>
                </div>

                {method === 'request' && (
                  <div className="space-y-2 pt-2" data-testid="cart-request-fields">
                    <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" className="w-full input-field px-3 py-2 text-sm" data-testid="cart-name" />
                    <input value={email} onChange={(e) => setEmail(e.target.value)} required type="email" placeholder="Email" className="w-full input-field px-3 py-2 text-sm" data-testid="cart-email" />
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know? (optional)" rows={3} className="w-full input-field px-3 py-2 text-sm" data-testid="cart-notes" />
                  </div>
                )}

                {error && <p className="text-xs text-red-300" data-testid="cart-error">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || !isAuthed}
                  className="w-full btn-primary px-5 py-3 text-sm inline-flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="cart-checkout-submit"
                >
                  {submitting ? (
                    <span>Working…</span>
                  ) : method === 'stripe' ? (
                    <><CreditCard className="w-4 h-4" /><span>Pay ${total.toFixed(2)}</span></>
                  ) : (
                    <><Send className="w-4 h-4" /><span>Send request</span></>
                  )}
                </button>
              </form>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
